// Carer picker for bulk SR reassign (Q10 — DESIGN-DECISIONS-2026-05-01).
// Loads /carers, presents a select, returns the picked carer_id.

import { useState } from "react";
import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../lib/api.js";
import { Modal } from "./Modal.js";

interface Carer { _id: string; full_name: string }
interface CarersResponse { data: Carer[]; total: number; page: number; limit: number }

interface Props {
  affectedCount: number;
  busy?: boolean;
  onConfirm: (carerId: string) => void;
  onClose: () => void;
}

function CarerSelect({
  isLoading, isError, carers, carerId, onChange,
}: {
  isLoading: boolean; isError: boolean; carers: readonly Carer[];
  carerId: string; onChange: (id: string) => void;
}): JSX.Element {
  if (isLoading) return <p className="text-sm text-slate-400">Loading carers…</p>;
  if (isError)   return <p role="alert" className="text-sm text-red-600">Failed to load carers.</p>;
  return (
    <select
      value={carerId}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
      aria-label="Select carer"
    >
      <option value="">— Select carer —</option>
      {carers.map((c) => (
        <option key={c._id} value={c._id}>{c.full_name}</option>
      ))}
    </select>
  );
}

export function CarerPickerDialog({ affectedCount, busy = false, onConfirm, onClose }: Props): JSX.Element {
  const [carerId, setCarerId] = useState("");
  const { data, isLoading, isError } = useQuery<CarersResponse>({
    queryKey: ["carers", "picker"],
    queryFn: () => apiFetch<CarersResponse>("/carers?limit=200"),
  });
  const carers = data?.data ?? [];
  const plural = affectedCount === 1 ? "" : "s";

  return (
    <Modal title="Reassign carer" onClose={onClose}>
      <p className="text-sm text-slate-600 mb-3">
        Reassigning <strong>{affectedCount}</strong> service request{plural}.
      </p>
      <CarerSelect isLoading={isLoading} isError={isError} carers={carers}
        carerId={carerId} onChange={setCarerId} />
      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 text-sm rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm(carerId)}
          disabled={busy || !carerId}
          className="px-4 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800 disabled:opacity-50"
        >
          {busy ? "Reassigning…" : "Reassign"}
        </button>
      </div>
    </Modal>
  );
}
